/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2025-01-09
 * @description Manages the report informations and listings
 */
import {
  Box,
  Button,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";

import { ReportList } from "./ReportList";
import { useFetchData } from "../../components/hooks/useFetchData";

const ReportPage = () => {
  const { fetchData, loading } = useFetchData();
  const ssid = useSelector((state) => state.auth.ssid);

  const today = new Date();
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(today.getDate() - 7);

  const [minLoading, setMinLoading] = React.useState(true);
  const [dateRange, setDateRange] = React.useState({
    startDate: oneWeekAgo.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  });
  const [reportData, setReportData] = React.useState([]);

  const getReportData = async () => {
    if (dateRange.endDate < dateRange.startDate) return;
    const body = {
      startdate: dateRange.startDate,
      enddate: dateRange.endDate,
    };
    const result = await fetchData("convertedleadsreport", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setReportData(result.data);
    }
  };

  React.useEffect(() => {
    getReportData();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadReport = async () => {
    if (dateRange.endDate < dateRange.startDate) return;
    const baseUrl = process.env.REACT_APP_API_URL;
    const imagePath = `export-convertedleads?startdate=${dateRange.startDate}&enddate=${dateRange.endDate}`;
    const urlWithAuth = `${baseUrl}${imagePath}&ssid=${ssid}`;

    window.open(urlWithAuth, "_blank");
  };

  const isLoading = minLoading || loading["convertedleadsreport"];
  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      {isLoading ? (
        <Skeleton variant="rounded" width={180} height={24} sx={{ mb: 2 }} />
      ) : (
        <Stack
          pb={1}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontWeight={700} fontSize={20}>
            Candidates Report
          </Typography>
        </Stack>
      )}
      {isLoading ? (
        <Skeleton
          variant="rounded"
          width={{ xs: "100%", sm: "40%" }}
          height={40}
          sx={{ mb: 2 }}
        />
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="start"
          gap={2}
          my={2}
          pb={2}
        >
          <TextField
            label="Start date"
            slotProps={{
              inputLabel: { shrink: true },
            }}
            variant="outlined"
            size="small"
            placeholder="Start date"
            type="date"
            sx={{
              width: { xs: "100%", sm: "auto" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            value={dateRange.startDate}
            onChange={(e) => {
              setDateRange((prev) => {
                return { ...prev, startDate: e.target.value };
              });
            }}
          />
          <TextField
            label="End date"
            slotProps={{
              inputLabel: { shrink: true },
            }}
            variant="outlined"
            size="small"
            placeholder="End date"
            type="date"
            sx={{
              width: { xs: "100%", sm: "auto" },

              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            value={dateRange.endDate}
            onChange={(e) => {
              setDateRange((prev) => {
                return { ...prev, endDate: e.target.value };
              });
            }}
            error={dateRange.endDate < dateRange.startDate}
            helperText={
              dateRange.endDate < dateRange.startDate
                ? "End date should be greater than start date"
                : ""
            }
          />

          <Button
            variant="contained"
            sx={{
              borderRadius: "12px",
              width: { xs: "100%", sm: "auto" },
            }}
            disabled={
              !dateRange.startDate ||
              !dateRange.endDate ||
              dateRange.endDate < dateRange.startDate
            }
            onClick={getReportData}
          >
            Search
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "12px",
              width: { xs: "100%", sm: "auto" },
            }}
            endIcon={<FileDownloadOutlined />}
            disabled={
              !dateRange.startDate ||
              !dateRange.endDate ||
              dateRange.endDate < dateRange.startDate
            }
            onClick={downloadReport}
          >
            CSV
          </Button>
        </Stack>
      )}
      {!isLoading && reportData.totalCount && (
        <Typography>Total count: {reportData.totalCount}</Typography>
      )}
      <ReportList isLoading={isLoading} reportData={reportData.candidates} />
    </Box>
  );
};

export default ReportPage;
